import styles from './carrito-page.module.scss';
import React from "react";

import cart, { CartItem } from "../../services/cart";
import api from "../../services/api";

export interface CarritoPageProps {
  name?: string;
  classes?: string;
}

interface State {
  items: CartItem[];
  name: string;
  phone: string;
  note: string;
  sending: boolean;
  done: boolean;
  error?: string;
}

const money = (n: number) => `$${n.toLocaleString("es-MX")}`;

/**
 * Pantalla de carrito: lista de items (localStorage) + resumen y checkout.
 * Para terminar la compra se pide el teléfono; el pedido se envía por WhatsApp
 * al negocio (entrega **contra entrega**, se confirma de forma personal).
 */
export default class CarritoPage extends React.Component<CarritoPageProps, State> {
  static jsClass = "CarritoPage";
  static defaultProps: Partial<CarritoPageProps> = {
    name: "mbCarrito",
  };

  state: State = {
    items: [],
    name: "",
    phone: "",
    note: "",
    sending: false,
    done: false,
  };

  private unsubscribe?: () => void;

  componentDidMount() {
    this.setState({ items: cart.items() });
    this.unsubscribe = cart.subscribe((items) => this.setState({ items }));
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  private onQty = (slug: string, delta: number) => {
    const item = this.state.items.find((i) => i.slug === slug);
    if (!item) return;
    cart.setQty(slug, item.qty + delta);
  };

  private onRemove = (slug: string) => cart.remove(slug);

  private onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const phone = this.state.phone.trim();
    if (!phone) {
      this.setState({ error: "Necesitamos tu número de teléfono para confirmar la compra." });
      return;
    }
    if (this.state.items.length === 0) {
      this.setState({ error: "El carrito está vacío." });
      return;
    }

    this.setState({ sending: true, error: undefined });
    api
      .checkout({
        items: this.state.items.map((i) => ({
          slug: i.slug,
          nombre: i.nombre,
          precio: i.precio,
          unidad: i.unidad,
          qty: i.qty,
        })),
        phone,
        name: this.state.name.trim() || undefined,
        note: this.state.note.trim() || undefined,
      })
      .then(() => {
        cart.clear();
        this.setState({ sending: false, done: true });
      })
      .catch((err) =>
        this.setState({ sending: false, error: String(err?.message ?? err) })
      );
  };

  render(): React.ReactNode {
    const { classes } = this.props;
    const { items, name, phone, note, sending, done, error } = this.state;
    const total = items.reduce((s, i) => s + i.precio * i.qty, 0);
    const hasQuote = items.some((i) => i.precio === 0);

    return (
      <div className={styles['mb-cart-page'] + (classes ? ' ' + classes : '')}>
        <div className="container">
          <h1 className={styles['mb-cart-page__title']}>Tu carrito</h1>

          {done ? (
            <div className={styles['mb-cart__ok']}>
              <h3>¡Gracias por tu pedido!</h3>
              <p>
                Te contactaremos por WhatsApp para confirmar la compra. La entrega
                es <strong>contra entrega</strong>.
              </p>
            </div>
          ) : items.length === 0 ? (
            <p className={styles['mb-cart-page__empty']}>
              Tu carrito está vacío. Agrega productos desde el catálogo o el recetario.
            </p>
          ) : (
            <div className={styles['mb-cart']}>
              {/* ── Items ─────────────────────────────────────────────── */}
              <ul className={styles['mb-cart__list']}>
                {items.map((it) => (
                  <li key={it.slug} className={styles['mb-cart__item']}>
                    {it.imagen ? (
                      <img className={styles['mb-cart__img']} src={it.imagen} alt={it.nombre} />
                    ) : (
                      <span className={styles['mb-cart__img']} />
                    )}
                    <div className={styles['mb-cart__info']}>
                      <p className={styles['mb-cart__name']}>{it.nombre}</p>
                      {it.unidad && <p className={styles['mb-cart__unit']}>por {it.unidad}</p>}
                      <div className={styles['mb-cart__qty']}>
                        <button type="button" onClick={() => this.onQty(it.slug, -1)} aria-label="Quitar uno">−</button>
                        <span>{it.qty}</span>
                        <button type="button" onClick={() => this.onQty(it.slug, 1)} aria-label="Agregar uno">+</button>
                      </div>
                    </div>
                    <div className={styles['mb-cart__right']}>
                      <span className={styles['mb-cart__price']}>
                        {it.precio > 0 ? money(it.precio * it.qty) : "Por cotizar"}
                      </span>
                      <button
                        type="button"
                        className={styles['mb-cart__remove']}
                        onClick={() => this.onRemove(it.slug)}>
                        Quitar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              {/* ── Resumen + checkout ────────────────────────────────── */}
              <form className={styles['mb-cart__summary']} onSubmit={this.onSubmit}>
                <p className={styles['mb-cart__total']}>
                  Total <span>{money(total)} <small>MXN</small></span>
                </p>

                {error && <p className={styles['mb-cart__error']}>{error}</p>}

                <input
                  className={styles['mb-cart__field']}
                  type="text"
                  placeholder="Tu nombre (opcional)"
                  value={name}
                  onChange={(e) => this.setState({ name: e.target.value })}
                  autoComplete="name"
                />
                <input
                  className={styles['mb-cart__field']}
                  type="tel"
                  placeholder="Tu número de teléfono *"
                  value={phone}
                  onChange={(e) => this.setState({ phone: e.target.value })}
                  autoComplete="tel"
                  required
                />
                <input
                  className={styles['mb-cart__field']}
                  type="text"
                  placeholder="Nota para tu pedido (opcional)"
                  value={note}
                  onChange={(e) => this.setState({ note: e.target.value })}
                />

                <p className={styles['mb-cart__note']}>
                  Por el momento la compra es <strong>contra entrega</strong> y se
                  terminará de forma personal por <strong>WhatsApp</strong>; por eso
                  necesitamos tu número de teléfono.
                  {hasQuote && " Los productos sin precio se cotizan al confirmar."}
                </p>

                <button
                  type="submit"
                  className={styles['mb-cart__submit']}
                  disabled={sending}>
                  {sending ? "Enviando…" : "Enviar pedido por WhatsApp"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }
}
